import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import CircularProgress from "@material-ui/core/CircularProgress";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import { parseISO, format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { statusLabel, statusColor } from "./statusMeta";
import SolicitacaoDetailDialog from "../SolicitacaoDetailDialog";

const drawerWidth = 340;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		padding: theme.spacing(2),
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(1),
	},
	headerActions: {
		display: "flex",
		alignItems: "center",
	},
	centered: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(4, 2),
		color: theme.palette.text.secondary,
	},
	listItem: {
		border: "1px solid rgba(0, 0, 0, 0.08)",
		borderRadius: 8,
		marginBottom: theme.spacing(1),
		flexDirection: "column",
		alignItems: "flex-start",
	},
	listItemTop: {
		display: "flex",
		width: "100%",
		alignItems: "center",
		justifyContent: "space-between",
	},
	number: {
		fontWeight: 600,
	},
	observation: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		maxWidth: "100%",
	},
}));

// Ao trocar de ticket ou fechar/reabrir, so recarrega nesses momentos - nunca
// polling, nunca websocket (fora de escopo da Etapa 4). O ignore-flag no
// useEffect evita que a resposta de um ticket antigo apareca depois de
// trocar rapido para outro (a troca muda ticketId, o efeito anterior e
// "descartado" antes do novo comecar).
const SolicitacoesDrawer = ({ open, onClose, ticketId }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [solicitacoes, setSolicitacoes] = useState([]);
	const [selectedId, setSelectedId] = useState(null);
	const [reloadFlag, setReloadFlag] = useState(0);

	const fetchSolicitacoes = useCallback(() => {
		let ignore = false;

		if (!ticketId) return () => {};

		setLoading(true);
		api
			.get("/solicitacoes", { params: { ticketId, pageSize: 100 } })
			.then(({ data }) => {
				if (!ignore) setSolicitacoes(data.solicitacoes);
			})
			.catch(err => {
				if (!ignore) toastError(err);
			})
			.finally(() => {
				if (!ignore) setLoading(false);
			});

		return () => {
			ignore = true;
		};
	}, [ticketId]);

	useEffect(() => {
		if (!open) return undefined;
		return fetchSolicitacoes();
	}, [open, ticketId, reloadFlag, fetchSolicitacoes]);

	const handleRefresh = () => setReloadFlag(flag => flag + 1);

	const handleStatusUpdated = () => {
		setReloadFlag(flag => flag + 1);
	};

	return (
		<>
			<Drawer
				id="solicitacoes-drawer-root"
				className={classes.drawer}
				variant="temporary"
				anchor="right"
				open={open}
				onClose={onClose}
				classes={{ paper: classes.drawerPaper }}
			>
				<div className={classes.header}>
					<Typography variant="h6">
						{i18n.t("solicitacoes.drawer.title")}
					</Typography>
					<div className={classes.headerActions}>
						<Tooltip title={i18n.t("solicitacoes.drawer.refresh")}>
							<IconButton size="small" onClick={handleRefresh} disabled={loading}>
								<RefreshIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<IconButton size="small" onClick={onClose}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</div>
				</div>
				<Divider />

				{loading && (
					<div id="solicitacoes-drawer-loading" className={classes.centered}>
						<CircularProgress size={28} />
					</div>
				)}

				{!loading && solicitacoes.length === 0 && (
					<div id="solicitacoes-drawer-empty" className={classes.centered}>
						<Typography variant="body2">
							{i18n.t("solicitacoes.drawer.empty")}
						</Typography>
					</div>
				)}

				{!loading && solicitacoes.length > 0 && (
					<List>
						{solicitacoes.map(item => {
							const color = statusColor(item.status);
							return (
								<ListItem
									key={item.id}
									id={`solicitacao-item-${item.number}`}
									button
									className={classes.listItem}
									onClick={() => setSelectedId(item.id)}
								>
									<div className={classes.listItemTop}>
										<Typography variant="body2" className={classes.number}>
											#{item.number} — {item.type?.name || "-"}
										</Typography>
										<Chip
											size="small"
											label={statusLabel(item.status)}
											style={{
												backgroundColor: color.background,
												color: color.color,
												fontWeight: 600,
											}}
										/>
									</div>
									<ListItemText
										primary={
											<Typography variant="caption" color="textSecondary">
												{format(parseISO(item.created_at), "dd/MM/yyyy HH:mm")}
												{item.attendant_name ? ` · ${item.attendant_name}` : ""}
											</Typography>
										}
										secondary={
											item.observation ? (
												<Typography
													variant="caption"
													className={classes.observation}
												>
													{item.observation}
												</Typography>
											) : null
										}
									/>
								</ListItem>
							);
						})}
					</List>
				)}
			</Drawer>

			{selectedId && (
				<SolicitacaoDetailDialog
					open={!!selectedId}
					solicitacaoId={selectedId}
					onClose={() => setSelectedId(null)}
					onStatusUpdated={handleStatusUpdated}
				/>
			)}
		</>
	);
};

export default SolicitacoesDrawer;
